import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Fade } from "@progress/kendo-react-animation";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { TimePicker } from "@progress/kendo-react-dateinputs";
import { useLocalization } from "@progress/kendo-react-intl";
import { Avatar } from "@progress/kendo-react-layout";
import { Notification, NotificationGroup } from "@progress/kendo-react-notification";
import { plusIcon, saveIcon, trashIcon } from "@progress/kendo-svg-icons";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { AccessToken } from "../../../config/AccessToken";
import { loadingPanel } from "../../LoadingPanel";
import { useNavigate } from "react-router-dom";

/**
 * ConfigRulesTimeslot is a React functional component that manages the creation,
 * updating, and deletion of time slots. It provides a user interface for adding 
 * new time slots, editing existing ones, and removing them. The component uses 
 * various state hooks to manage form inputs and the list of time slots. It also 
 * handles authentication and authorization for API requests to the server, 
 * providing notifications for success and error states during these operations.
 */

export const ConfigRulesTimeslot = () => {

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const [instance, accessTokenRequest] = AccessToken();

    let languageId = localStorage.getItem('languageId');
    const localizationService = useLocalization();

    const userType = localStorage.getItem('userType');

    const [state, setState] = useState<any>({
        id: 0,
        startDate: '',
        endDate: ''
    });

    const [selectedStartDate, setSelectedStartDate] = useState(new Date());
    const [selectedEndDate, setSelectedEndDate] = useState(new Date());
    const [timeslotIdSelected, setTimeslotIdSelected] = useState(-1);

    const [newTimeSlot, setNewTimeSlot] = useState<any>([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(false);

    const navigate = useNavigate()

    useEffect(() => {
        loadTimeslot();
    }, []);

    const convertDateTime = (dateTime: any) => {

        if (dateTime == null) {
            return null
        }

        const [datePart, timePart] = dateTime?.split(' ');
        const [day, month, year] = datePart?.split('/');
        const [hour, minute, seconds] = timePart?.split(':');

        const date = new Date(Date.UTC(year, month - 1, day, hour, minute, seconds))

        return date
    }

    const formatDateFromIsoToString = (date: any) => {
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
            return '';
        }
        
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');

        if (languageId == 'en-US') {
            hours = hours % 12;
            hours = hours === 0 ? 12 : hours; // convert 0 → 12
            const hours12 = String(hours).padStart(2, '0');
            return `${hours12}:${minutes}`;
        } 
        else {
            const hours24 = String(hours).padStart(2, '0');
            return `${hours24}:${minutes}`;
        }
    }

    const toTimePickerDate = (value: any, fallback: Date = new Date()) => {
        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            return value;
        }

        if (typeof value === 'string') {
            const trimmed = value.trim();
            const timeMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);

            if (timeMatch) {
                const [, hours, minutes, seconds = '0'] = timeMatch;
                const date = new Date(fallback);
                date.setHours(Number(hours), Number(minutes), Number(seconds), 0);
                return date;
            }

            const parsed = new Date(trimmed);
            if (!Number.isNaN(parsed.getTime())) {
                return parsed;
            }
        }

        return fallback;
    };

    const loadTimeslot = async () => {
        
        setLoading(true);

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/timeslot/list`, { headers })
                .then((res) => {
                    const data: any = res?.data?.data
                    const timeslotList = (data ?? []).map((timeslot: any) => ({
                        ...timeslot,
                        startDate: formatDateFromIsoToString(convertDateTime(timeslot?.startDate)),
                        endDate: formatDateFromIsoToString(convertDateTime(timeslot?.endDate))
                    }));

                    setNewTimeSlot(timeslotList);

                    navigate(`/home/configuration/rules/timeslot`);

                }).catch((data) => {
                    setError(data?.message);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }

        setLoading(false);
    }

    const handleChange = (event: any) => {
        let name = event.target.name || event.target.element.current.name;
        const rawValue = event?.value ?? event?.target?.value;
        let currentValue = toTimePickerDate(rawValue, name === 'endDate' ? selectedEndDate : selectedStartDate);
        
        setState({
            ...state,
            [name]: currentValue,
        });
        
        if (name == 'startDate') {
            const hour = String(currentValue.getHours()).padStart(2, '0');
            const minutes = String(currentValue.getMinutes()).padStart(2, '0');
            state.startDate = `${hour}:${minutes}`
            setSelectedStartDate(currentValue)
        }
        if (name == 'endDate') {
            const hour = String(currentValue.getHours()).padStart(2, '0');
            const minutes = String(currentValue.getMinutes()).padStart(2, '0');
            state.endDate = `${hour}:${minutes}`
            setSelectedEndDate(currentValue)
        }
    };

    const [saved, setSaved] = useState(false);
    const [removed, setRemoved] = useState(false);

    const toUtcIsoString = (value: Date | null | undefined) => {
        if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
            return '';
        }

        return value.toISOString().slice(0, 16) + 'Z';
    };

    const saveTimeslot = async () => {
        
        setLoading(true);

        const startDate = toUtcIsoString(selectedStartDate);
        const endDate = toUtcIsoString(selectedEndDate);

        
        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.post(`${SERVER_URL}/timeslot?startDate=${startDate}&endDate=${endDate}`, null, { headers })
                .then(() => {
                    let startDateFormatted:any = ''
                    let endDateFormatted:any = ''
                    let newTimeSlotFormatted:any = []
                    if (state?.startDate != null) {
                        const startDate = new Date(state?.startDate);
                        const hour = (startDate.getHours() < 10 ? '0' : '') + startDate.getHours();
                        const minutes = (startDate.getMinutes() < 10 ? '0' : '') + startDate.getMinutes();
                        startDateFormatted = `${hour}:${minutes}`
                    }
                    if (state?.endDate != null) {
                        const endDate = new Date(state?.endDate);
                        const hour = (endDate.getHours() < 10 ? '0' : '') + endDate.getHours();
                        const minutes = (endDate.getMinutes() < 10 ? '0' : '') + endDate.getMinutes();
                        endDateFormatted = `${hour}:${minutes}`
                    }
                    newTimeSlotFormatted = {
                        startDate: startDate,
                        endDate: endDateFormatted
                    }

                    setState({
                        ...state,
                        startDate: '',
                        endDate: ''
                    })
                    
                    setNewTimeSlot([...newTimeSlot, newTimeSlotFormatted]);

                    setSaved(true)                    

                    setLoading(false);

                    setTimeout(() => {
                        setSaved(false)
                    }, 3000);
                }).catch((data) => {
                    setError(data?.message);
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }

        if (!saved) {
            setTimeout(() => {
                setSaved(false);
            }, 3000);
        }

    }

    const updatedTimeslotSelected = async (e:any, timeslotId: any) => {
        e.preventDefault()
        
        setLoading(true);
        const startDateChanged = toUtcIsoString(selectedStartDate);
        const endDateChanged = toUtcIsoString(selectedEndDate);
        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            try {
                await axios.put(`${SERVER_URL}/timeslot/${timeslotId}?startDate=${startDateChanged}&endDate=${endDateChanged}`, null, { headers })
                setSaved(true)
                
                loadTimeslot()           
                setLoading(false);   
            } catch (error: any) {
                setError(error?.message);
                setLoading(false);
            }
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }

        if (!saved) {
            setTimeout(() => {
                setSaved(false);
            }, 3000);
        }

    }

    const removeTimeslot = async (timeslotId: any, index: any) => {
        
        setLoading(true);

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            axios.delete(`${SERVER_URL}/timeslot/${timeslotId}`, { headers })
                .then(() => {
                    setNewTimeSlot((oldValues: any) => {
                        return oldValues.filter((_: any, i: any) => i !== index)
                    })
                    setRemoved(true);
                    setLoading(false);
                }).catch((data) => {
                    if( data?.response.data.errorMessage == "Error executing the API: Error: Timeslot is connected to the subscription"){
                        if(languageId == 'it-IT'){
                            setError("Errore: Il timeslot è connesso alla sottoscrizione");
                        }else{
                            setError("Error: Timeslot is connected to the subscription");
                        }
                    }
                    //setError(data?.response.data.errorMessage);
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }

        if (!removed) {
            setTimeout(() => {
                setRemoved(false);
            }, 3000);
        }
        if (!error) {
            setTimeout(() => {
                setError(false);
            }, 5000);
        }

    }

    const loadSelectedTimeslot = (timeslot: any) => {
        
        let startDateConverted: any;
        let endDateConverted: any;
        let idTimeslot: any = timeslot.id;


        if (timeslot?.startDate != null) {
            let pieces: any = timeslot.startDate.split(':')
            let hours, minutes;
            if (pieces.length === 2) {
                hours = parseInt(pieces[0], 10);
                minutes = parseInt(pieces[1], 10);
            }
            startDateConverted = new Date();
            startDateConverted.setHours(hours)
            startDateConverted.setMinutes(minutes)
            setSelectedStartDate(startDateConverted);
        }
        if (timeslot?.endDate != null) {
            let pieces: any = timeslot.endDate.split(':')
            let hours: any, minutes: any;
            if (pieces.length === 2) {
                hours = parseInt(pieces[0], 10);
                minutes = parseInt(pieces[1], 10);
            }
            endDateConverted = new Date();
            endDateConverted.setHours(hours)
            endDateConverted.setMinutes(minutes)
            setSelectedEndDate(endDateConverted);
        }
        setTimeslotIdSelected(idTimeslot);

        // Access the clicked element's ref
        itemRefs?.current?.forEach((element:any, i:any) => {
            if (i == timeslot.id) {
              itemRefs.current[i].style.background = '#198792' ;   
              itemRefs.current[i].style.borderRadius = '10px' ;   
              itemRefs.current[i].style.color = '#ffffff' ;   
            }else{
                itemRefs?.current[i]?.style?.removeProperty('background')
                itemRefs?.current[i]?.style?.removeProperty('color')
            }
        });
    };

    const itemRefs:any = useRef([]);

    return (
            <div className= "flex-parent-element" >
                <NotificationGroup
                    style={{
                        right: 0,
                        bottom: 50,
                        alignItems: "flex-start",
                        flexWrap: "wrap-reverse",
                    }}
                >
                    <Fade enter={true} exit={true}>
                        {removed && (
                            <Notification
                                type={{ style: "success", icon: true }}
                                closable={true}
                                onClose={() => setRemoved(false)}
                            >
                                <span>{localizationService.toLanguageString('configuration.rules.timeslot_removed', 'NOT FOUND')}</span>
                            </Notification>
                        )}
                        {error && (
                            <Notification
                                type={{ style: "error", icon: true }}
                                closable={true}
                                onClose={() => setError(true)}
                            >
                                <span>{error}</span>
                            </Notification>
                        )}
                        {/*{created && (
                            <Notification
                                type={{ style: "success", icon: true }}
                                closable={true}
                                onClose={() => setCreated(false)}
                            >
                                <span>{localizationService.toLanguageString('configuration.rules.timeslot_updated', 'NOT FOUND')}</span>
                            </Notification>
                        )}*/}
                        {saved && (
                            <Notification
                                type={{ style: "success", icon: true }}
                                closable={true}
                                onClose={() => setSaved(false)}
                            >
                                <span>{localizationService.toLanguageString('configuration.rules.timeslot_saved', 'NOT FOUND')}</span>
                            </Notification>
                        )}
                    </Fade>
                </NotificationGroup>         

                {/*left panel*/}
                {loading && loadingPanel}
                <div 
                    style={{                        
                        padding: '20px',
                        //borderRight: '1px solid #1A8894', 
                        background: "#F7F7F7",
                        flex: '1 1 400px',
                        overflowY: 'auto'
                    }}
                >                
                    <div style={{ textAlign:'center', marginBottom:'20px' }}>
                        <p style={{ display:'inline-flex', textAlign: 'center', fontSize: '20px', color: '#1A8893' }}>{localizationService.toLanguageString('configuration.rules.timeslot', 'NOT_FOUND')} ({newTimeSlot.length})</p>
                        <Button
                            title={localizationService.toLanguageString('configuration.rules.timeslot_new', 'NOT_FOUND')}
                            themeColor={"primary"}
                            className="plusParam"
                            style={{
                                marginRight: '10px',
                                marginTop: '0px'
                            }}
                            disabled= {userType === 'StandardOnlyLocator'}
                            onClick={() => {
                                let resetTimeslot = -1 
                                setTimeslotIdSelected(resetTimeslot);
                                setState({ startDate: '', endDate: '' });
                                //refreshTimeslot(); 
                            }}
                        >
                            <Avatar
                                className="plusParam"
                            >
                                <SvgIcon
                                    icon={plusIcon}
                                    style={{ margin: '8px' }}
                                />
                            </Avatar>
                        </Button>
                    </div>     

                    
                    <div
                        className="flex-left-child-element"
                        style={{  background: "#F7F7F7", paddingLeft: "5%" }}
                    >
                    {newTimeSlot?.map((timeSlot: any, index: any) => {
                        return (
                            <div 
                                key={index} 
                            >
                                <div 
                                    className="trash-icon" 
                                    style={{
                                        float: 'left',
                                        cursor: 'pointer',
                                        alignContent: 'center',
                                        marginRight: '10px',
                                        pointerEvents: `${userType === 'StandardOnlyLocator' ? 'none' : 'auto'}`,
                                        opacity: `${userType === 'StandardOnlyLocator' ? 0.5 : 1}`
                                    }}
                                    title={localizationService.toLanguageString('configuration.rules.timeslot_remove', 'NOT FOUND')}
                                    onClick={() => removeTimeslot(timeSlot.id, index)} 
                                >
                                    <Avatar       
                                        className="trash-icon"     
                                    >
                                        <SvgIcon                                             
                                            icon={trashIcon} 
                                            style={{ verticalAlign: 'baseline', margin: '10px' }} 
                                        />
                                    </Avatar>
                                </div>
                                <p
                                    className="card-block-registry-group"
                                    ref={(el) => (itemRefs.current[timeSlot.id] = el)}
                                    style={{
                                        cursor: 'pointer',
                                        marginLeft: '10px',
                                        marginBottom: '0px',
                                        padding: '10px'
                                    }}
                                    onClick={() => loadSelectedTimeslot(timeSlot)}
                                >
                                    {timeSlot.startDate + ' - ' + timeSlot.endDate}
                                    {/*{timeSlot.id +": "+ timeSlot.startDate + ' - ' + timeSlot.endDate}*/}
                                    {/*<p>{timeSlot.startDate.toTimeString().split(' ')[0].slice(0, -3) + ' - ' + timeSlot.endDate.toTimeString().split(' ')[0].slice(0, -3)}</p>*/}
                                </p>
                            </div>
                            )
                        })}
                    </div>
                   
                </div>

                {/*right panel*/}
                <div 
                    className="flex-right-child-element"
                    style={{                         
                        background: 'white',
                        flex: '3 1 200px'
                    }}
                >
                    <form 
                        //className="k-form" 
                        //onSubmit={handleSubmit}
                    >
                        <fieldset>
                            <div style={{ display: 'inline'}}>
                                <div className="k-form-field">
                                    <p style={{ display: 'inline-block', width: '50px' }}>{localizationService.toLanguageString('configuration.rules.timeslot_from', 'NOT FOUND')}</p>
                                    <TimePicker
                                        format="HH:mm"
                                        name="startDate"
                                        validityStyles={false}
                                        value={selectedStartDate}
                                        width={150}
                                        required={true}
                                        onChange={handleChange}
                                        validationMessage={
                                            state.startDate === null
                                                ? `${localizationService.toLanguageString('configuration.rules.timeslot_from_is_required', 'NOT FOUND')}`
                                                : ""
                                        }
                                        disabled= {userType === 'StandardOnlyLocator'}
                                    />

                                    {timeslotIdSelected == -1 ?
                                        <Button
                                            title={localizationService.toLanguageString('configuration.registry.group_save', 'NOT FOUND')}
                                            themeColor={"primary"}
                                            className="saveParam"
                                            style={{ marginLeft: '20px' }}
                                            disabled={state.startDate == '' || state.endDate == '' || userType === 'StandardOnlyLocator'}
                                            onClick={()=>saveTimeslot()}
                                        >
                                            <Avatar
                                                className="saveParam"
                                            >
                                                <SvgIcon
                                                    icon={saveIcon}
                                                    style={{ margin: '8px' }}
                                                />
                                            </Avatar>
                                        </Button>
                                        :
                                        <div style={{ display: 'inline' }}>
                                            <Button
                                                title={localizationService.toLanguageString('configuration.rules.timeslot_update', 'NOT_FOUND')}
                                                themeColor={"primary"}
                                                className="saveParam"
                                                style={{ marginLeft: '20px' }}
                                                disabled={userType === 'StandardOnlyLocator'}
                                                onClick={(e:any) => updatedTimeslotSelected(e, timeslotIdSelected)}
                                            >
                                                <Avatar
                                                    className="saveParam"
                                                >
                                                    <SvgIcon
                                                        icon={saveIcon}
                                                        style={{ margin: '8px' }}
                                                    />
                                                </Avatar>
                                            </Button>
                                        </div>
                                    }

                                </div>
                                <div className="k-form-field">
                                    <p style={{ display: 'inline-block', width: '50px' }}>{localizationService.toLanguageString('configuration.rules.timeslot_to', 'NOT FOUND')}</p>
                                    <TimePicker
                                        format="HH:mm"
                                        name="endDate"
                                        validityStyles={false}
                                        value={selectedEndDate}
                                        width={150}
                                        required={true}                                        
                                        onChange={handleChange}
                                        validationMessage={
                                            state.endDate === null
                                                ? `${localizationService.toLanguageString('configuration.rules.timeslot_to_is_required', 'NOT FOUND')}`
                                                : `${localizationService.toLanguageString('configuration.rules.timeslot_to_cannot_be_before_from', 'NOT FOUND')}`
                                        }
                                        disabled= {userType === 'StandardOnlyLocator'}
                                    />
                                </div>
                            </div>
                        </fieldset>

                        <br />

                    </form> 
                   
                </div>
            </div>
    )
}